<template>
  <v-container fluid>
    <v-data-table
      :headers="visibleHeaders"
      :items="orderedSubordinates"
      :loading="subordinatesLoading"
      :search="searchText"
      :custom-filter="approverClockingFilter"
      :mobile-breakpoint="$vuetify.breakpoint.mobileBreakpoint"
      hide-default-footer
      show-select
      disable-pagination
      disable-sort
      class="elevation-1 mb-14"
      @current-items="updateActiveStatusSwitches"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title style="min-width: 80px">{{ title }}</v-toolbar-title>
          <v-divider
            inset
            vertical
            class="mx-4"
          />
          <v-spacer v-if="!isMobile()"/>
          <v-row justify="end">
            <v-col cols="auto">
              <v-text-field
                v-model="searchText"
                append-icon="search"
                :label="`${ $t('filters.search') }`"
                :outlined="!isMobile()"
                single-line
                clearable
                hide-details
                class="py-2"
                @input="searchChange"
              />
            </v-col>
          </v-row>
        </v-toolbar>
        <v-divider/>
      </template>
      <template v-slot:header.data-table-select>
        <v-checkbox
          v-model="allSubordinatesSelected"
          ripple
          hide-details
          class="mt-0 pt-0"
          @change="selectAllSubordinates"
        />
        <v-card-subtitle v-if="isMobile()">{{ $t('actions.selectAll') }}</v-card-subtitle>
      </template>
      <template v-slot:item.data-table-select="{ item }">
        <v-checkbox
          v-model="selectedSubordinates"
          :value="item.employee.id"
          :disabled="employeesAbsenceStatuses[item.employee.id]"
          multiple
          ripple
          hide-details
          class="mt-0 pt-0"
        />
      </template>
      <template v-slot:item.name="{ item }">
        {{ item.employee.firstName }} {{ item.employee.lastName }}
      </template>
      <template v-slot:item.info="{ item }">
        <TimePairStatusIndicator :items="getStatusIndicators(item)"/>
      </template>
      <template v-slot:item.position="{ item }">
        <div v-if="!!item.activeTimePair">
          {{ item.activeTimePair.employeeAssignment.position.description }}
        </div>
        <div class="font-italic" v-else>
          {{ $tc('indicators.positionsAvailable', item.subordinateAssignments.length) }}
        </div>
      </template>
      <template v-slot:item.clockIn="{ item }">
        {{
          item.activeTimePair ? convertToDisplayedClockIn(item.activeTimePair.clockIn.date) : '-'
        }}
      </template>
      <template v-slot:item.duration="{ item }">
        {{ item.activeTimePair ? convertToDisplayedDuration(item.activeTimePair) : '--:--' }}
      </template>
      <template v-slot:item.details="{ item }">
        <v-tooltip top :disabled="isMobile()">
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isLocationFeatureActive"
              v-on="on"
              v-show="!isActiveTimePairNull(item.activeTimePair) && hasLocationData(item.activeTimePair)"
              :class="actionIcon"
              @click="viewTimePairMap(item)"
            >
              location_on
            </v-icon>
          </template>
          <span> {{ $t('indicators.showGeoData')}}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.status="{ item }">
        <v-row justify="center" align="center" no-gutters>
          <span class="mr-3 caption">{{ $t('misc.out') }}</span>
          <v-switch
            v-model="statusSwitchClockedInTimePairs"
            :value="item.activeTimePair"
            :color="subordinateHasActiveSegment(item.activeTimePair) ? 'warning' : 'success'"
            :error="isActiveTimePairNull(item.activeTimePair)"
            :disabled="isNotManagerOfClockIn(item)"
            multiple
            inset
            hide-details
            class="mt-0 pt-0 approverClockingSwitch"
            @click="performClockingStatusChange(item)"
          />
          <span class="ml-1 caption"> {{ $t('misc.in') }}</span>
        </v-row>
      </template>
    </v-data-table>

    <v-bottom-navigation fixed grow dark background-color="primary">
      <v-card v-if="!isMenuDrawerClosable()" flat disabled width="256px" color="secondary"/>

      <v-btn
        :disabled="clockInSelectedDisabled"
        text
        @click="clockInSelected"
      >
        <small v-if="isMobile()">{{ $t('misc.selected')}}</small>
        <small v-if="isMobile()">{{ $t('clock.clockIn') }}</small>
        <span v-else>{{ $t('clock.clockInSelected') }}</span>
        <v-icon>alarm</v-icon>
      </v-btn>
      <v-btn
        :disabled="clockOutSelectedDisabled"
        text
        @click="clockOutSelected(false)"
      >
        <small v-if="isMobile()">{{ $t('misc.selected')}}</small>
        <small v-if="isMobile()">{{ $t('clock.clockOut') }}</small>
        <span v-else>{{ $t('clock.clockOutSelected') }}</span>
        <v-icon>alarm_off</v-icon>
      </v-btn>

      <v-spacer v-if="$vuetify.breakpoint.name !== 'xs'"/>

      <v-btn
        :disabled="clockOutAllDisabled"
        text
        @click="clockOutSelected(true)"
      >
        <small v-if="isMobile()">{{ $t('misc.all')}}</small>
        <small v-if="isMobile()">{{ $t('clock.clockOut') }}</small>
        <span v-else>{{ $t('clock.clockOutAll')}}</span>
        <v-icon>alarm_off</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <ApprovalsClockInDialog
      :value="clockInDialog"
      :subordinates="clockInSelectedSubordinates"
      @close="closeClockIn"
      @clock-in-submitted="clockInSuccessCallback"
    />

    <TimePairMapDialog
      :value="timePairMapDialog"
      :timePair="viewingTimePair"
      @close="timePairMapDialog = false"
    />
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import last from 'lodash/last';
import map from 'lodash/map';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';
import without from 'lodash/without';

import constants from '@/constants';

import DateTimeUtil from '@/util/date-time-util';

import canClockMassShiftOut from '@/mixins/clocking/mass/canClockMassShiftOut';
import hasLocationData from '@/mixins/location/hasLocationData';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import hasActionIcon from '@/mixins/hasActionIcon';
import hasAppDialog from '@/mixins/hasAppDialog';
import hasAppLoader from '@/mixins/hasAppLoader';
import displayAdaptable from '@/mixins/displayAdaptable';
import resolveMobileBackButton from '@/mixins/resolveMobileBackButton';

import ApprovalsClockInDialog from '@/components/approvals/ApprovalsClockInDialog.vue';
import TimePairMapDialog from '@/components/timepairs/TimePairMapDialog.vue';
import TimePairStatusIndicator from '@/components/timepairs/TimePairStatusIndicator.vue';
import i18n from '@/plugins/i18n';
import AuthorisationUtil from '@/util/authorisation-util';
import hasAssignmentFeatures from '@/mixins/clocking/hasAssignmentFeatures';

export default {
  name: 'ApproverClocking',

  components: {
    TimePairStatusIndicator,
    TimePairMapDialog,
    ApprovalsClockInDialog,
  },

  mixins: [
    canClockMassShiftOut,
    displayAdaptable,
    hasActionIcon,
    hasAppDialog,
    hasAppLoader,
    hasLocationData,
    hasLocationFeature,
    hasAppSnackbar,
    hasAssignmentFeatures,
    resolveMobileBackButton,
  ],

  data() {
    return {
      subordinatesLoading: false,
      clockingHeaders: [
        {
          text: this.isMobile() ? this.$t('tables.select') : '',
          value: constants.DataTableHeaderValues.DATA_TABLE_SELECT,
          sortable: false,
        },
        {
          text: this.$t('tableHeaders.name'),
          value: constants.DataTableHeaderValues.NAME,
        },
        {
          text: 'Info',
          value: constants.DataTableHeaderValues.INFO,
          sortable: false,
        },
        {
          text: this.$t('tableHeaders.position'),
          value: constants.DataTableHeaderValues.POSITION,
          hidden: !this.isConcurrentEmploymentActive,
        },
        {
          text: this.$t('tableHeaders.clockInDate'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
        },
        {
          text: this.$t('tableHeaders.details'),
          value: constants.DataTableHeaderValues.DETAILS,
        },
        {
          text: this.$t('tableHeaders.status'),
          value: constants.DataTableHeaderValues.STATUS,
          align: 'center',
        },
      ],
      statusSwitchClockedInTimePairs: [],
      allSubordinatesSelected: false,
      selectedSubordinates: [],
      searchText: '',
      clockInSelectedDisabled: true,
      clockOutSelectedDisabled: true,
      clockInDialog: false,
      clockInSelectedSubordinates: [],
      clockInSuccessCallback: Function,
      timePairMapDialog: false,
      viewingTimePair: {},
      decodedAuthToken: null,
      pingServerTimeout: null,
    };
  },

  computed: {
    ...mapState(['subordinateEmployees', 'employeeAbsences']),

    title() {
      return this.isMobile() ? this.$tc('tableHeaders.shift', 2) : this.$t(
        'tableHeaders.employeeShifts',
      );
    },

    visibleHeaders() {
      return this.clockingHeaders.filter((header) => !header.hidden);
    },

    orderedSubordinates() {
      return sortBy(this.subordinateEmployees, [
        (sub) => {
          if (isNull(sub.activeTimePair)) {
            return 1;
          }
          return -1;
        },
        (sub) => sub.employee.firstName,
      ]);
    },

    clockOutAllDisabled() {
      return this.getClockedInSubordinates(this.subordinateEmployees).length <= 0;
    },

    employeesAbsenceStatuses() {
      const absences = {};
      this.subordinateEmployees.forEach(
        // eslint-disable-next-line
        (sub) => absences[sub.employee.id] = this.employeeAbsences[sub.subordinateAssignments[0].id] !== undefined,
      );

      return absences;
    },
  },

  watch: {
    subordinateEmployees() {
      this.statusSwitchClockedInTimePairs = [];
      forEach(this.subordinateEmployees, (sub) => {
        if (sub.activeTimePair) {
          this.statusSwitchClockedInTimePairs.push(sub.activeTimePair);
        }
      });
    },

    selectedSubordinates() {
      this.setClockInSelectedDisabled();
      this.setClockOutSelectedDisabled();
      this.setAllSubordinatesSelected();
    },
  },

  methods: {
    ...mapActions([
      'loadSubordinateEmployees',
      'saveShiftTimeEntries',
      'loadEmployeeAbsences',
      'loadSubordinateEmployeesActiveShift',
    ]),
    ...mapMutations([
      'setPageTitle',
      'updateSubordinateEmployeeActiveTimePair',
      'resetSubordinateEmployeeActiveTimePair',
    ]),

    setClockInSelectedDisabled() {
      const inactiveTimePairs = this.getClockedInSubordinates(this.getSelectedSubordinatesDetails());

      this.clockInSelectedDisabled = inactiveTimePairs.length === this.selectedSubordinates.length;
    },

    setClockOutSelectedDisabled() {
      const activeTimePairs = this.getClockedOutSubordinates(this.getSelectedSubordinatesDetails());

      this.clockOutSelectedDisabled = activeTimePairs.length === this.selectedSubordinates.length;
    },

    updateActiveStatusSwitches(currentSubordinates) {
      this.statusSwitchClockedInTimePairs = map(
        filter(currentSubordinates, (sub) => !this.isActiveTimePairNull(sub.activeTimePair)),
        (sub) => sub.activeTimePair,
      );
    },

    searchChange() {
      this.clearSelections(this.subordinateEmployees);
    },

    getStatusIndicators(subordinate) {
      if (!subordinate.activeTimePair) return [];

      const indicators = [];

      if (this.isLocationFeatureActive && !this.hasLocationData(subordinate.activeTimePair)) {
        indicators.push({
          id: 'location',
          icon: 'location_off',
          color: constants.AppColors.WARN,
          tooltip: this.$t('indicators.noGeoData'),
        });
      }

      if (this.isNotManagerOfClockIn(subordinate)) {
        indicators.push({
          id: 'not_manager',
          icon: 'assignment_late',
          color: constants.AppColors.WARN,
          tooltip: 'Employee is clocked in with a position for which you are not an approver',
        });
      }

      if (this.subordinateHasActiveSegment(subordinate.activeTimePair)) {
        indicators.push({
          id: 'active_segment',
          icon: 'emoji_food_beverage',
          color: constants.AppColors.PRIMARY,
          tooltip: i18n.t('indicators.currentlyAway'),
        });
      }

      return indicators;
    },

    isActiveTimePairNull(timePair) {
      return isNull(timePair);
    },

    isNotManagerOfClockIn(subordinate) {
      return (!!subordinate.activeTimePair
        && !some(
          subordinate.subordinateAssignments,
          { id: subordinate.activeTimePair.employeeAssignment.id },
        )
      );
    },

    convertToDisplayedClockIn(dateTime) {
      return DateTimeUtil.convertToHumanReadableDateTime(dateTime);
    },

    convertToDisplayedDuration(timePair) {
      return DateTimeUtil.getFormattedDuration(timePair.roundedDuration);
    },

    subordinateHasActiveSegment(shiftTimePair) {
      if (shiftTimePair && !isEmpty(shiftTimePair.segmentTimePairs)) {
        return last(shiftTimePair.segmentTimePairs).clockOut == null;
      }
      return false;
    },

    approverClockingFilter(_, search, item) {
      search = search.toString().toLowerCase();

      const nameMatch = (item.employee.firstName.toLowerCase().includes(search)
        || item.employee.lastName.toLowerCase().includes(search));

      let assignmentMatch = false;
      let dateMatch = false;
      let durationMatch = false;

      if (item.activeTimePair) {
        assignmentMatch = item.activeTimePair.employeeAssignment.position.description.toLowerCase()
        .includes(search);
        dateMatch = this.convertToDisplayedClockIn(item.activeTimePair.clockIn.date)
        .toLowerCase()
        .includes(search);
        durationMatch = this.convertToDisplayedDuration(item.activeTimePair).toLowerCase()
        .includes(search);
      }

      if (nameMatch || assignmentMatch || dateMatch || durationMatch) return item;
      return false;
    },

    selectAllSubordinates() {
      if (this.allSubordinatesSelected) {
        this.selectedSubordinates = map(
          this.subordinateEmployees,
          (subordinate) => subordinate.employee.id,
        ).filter((sub) => !this.employeesAbsenceStatuses[sub]);
      } else {
        this.selectedSubordinates = [];
      }
    },

    setAllSubordinatesSelected() {
      this.allSubordinatesSelected = this.subordinateEmployees.length === this.selectedSubordinates.length;
    },

    getSelectedSubordinatesDetails() {
      return filter(
        this.subordinateEmployees,
        (subordinate) => this.selectedSubordinates.includes(subordinate.employee.id),
      );
    },

    getClockedInSubordinates(subordinates) {
      return filter(subordinates, (subordinate) => !isNull(subordinate.activeTimePair));
    },

    getClockedOutSubordinates(subordinates) {
      return filter(subordinates, (subordinate) => isNull(subordinate.activeTimePair));
    },

    performClockingStatusChange(subordinate) {
      this.statusSwitchClockedInTimePairs = without(this.statusSwitchClockedInTimePairs, null);
      if (subordinate.activeTimePair) {
        this.statusSwitchClockedInTimePairs.push(subordinate.activeTimePair);
      }

      if (isNull(subordinate.activeTimePair)) {
        this.individualClockIn(subordinate);
      } else {
        this.individualClockOut(subordinate);
      }
    },

    individualClockIn(subordinate) {
      this.clockInSelectedSubordinates = [subordinate];
      this.clockInSuccessCallback = this.completeIndividualClockIn;
      this.clockInDialog = true;
    },

    completeIndividualClockIn(responseData) {
      this.updateSubordinateEmployeeActiveTimePair({
        employeeId: this.clockInSelectedSubordinates[0].employee.id,
        timePair: responseData[0],
      });

      this.showSnackbar({
        text: this.$t('snackbar.clockInSuccess', {
          firstName: this.clockInSelectedSubordinates[0].employee.firstName,
          lastName: this.clockInSelectedSubordinates[0].employee.lastName,
        }),
        color: constants.AppColors.SUCCESS,
      });

      this.clearSelections([this.clockInSelectedSubordinates[0]]);

      this.closeClockIn();
    },

    closeClockIn() {
      this.clockInSelectedSubordinates = [];
      this.clockInSuccessCallback = Function;
      this.clockInDialog = false;
    },

    individualClockOut(subordinate) {
      this.showAppConfirmDialog({
        message: `${this.$t('dialogs.confirm.clockOutEmployee',
          {
            firstName: subordinate.employee.firstName,
            lastName: subordinate.employee.lastName,
          })}`,
        confirmText: this.$t('clock.clockOut'),
        confirmHandler: () => {
          this.showAppLoader(this.$tc('appLoader.clockingOutEmp', 1));

          this.createMassShiftClockOut(
            [{
              shiftTimePairId: subordinate.activeTimePair.id,
              timeZoneOffset: subordinate.activeTimePair.employeeAssignment.timeZoneOffset,
            }],
            (responseData) => this.completeIndividualClockOut(responseData, subordinate),
            () => {
              this.clearSelections([subordinate]);
              this.hideAppLoader();
            },
            () => this.resetSubordinateEmployeeActiveTimePair({ employeeId: subordinate.employee.id }),
          );
        },
      });
    },

    completeIndividualClockOut(responseData, subordinate) {
      if (responseData.length === 0) {
        this.showAppErrorDialog({
          message: i18n.t('dialogs.error.causes.shiftAlreadyClockedOut'),
        });
        this.resetSubordinateEmployeeActiveTimePair({ employeeId: subordinate.employee.id });
      } else {
        this.updateSubordinateEmployeeActiveTimePair(
          { employeeId: subordinate.employee.id, timePair: responseData[0] },
        );

        this.showSnackbar({
          text: this.$t('snackbar.clockOutSuccess', {
            firstName: subordinate.employee.firstName,
            lastName: subordinate.employee.lastName,
          }),
          color: constants.AppColors.SUCCESS,
        });
      }
    },

    clockInSelected() {
      const clockInSubordinates = this.getClockedOutSubordinates(this.getSelectedSubordinatesDetails());

      if (clockInSubordinates.length === 1) {
        this.individualClockIn(clockInSubordinates[0]);
      } else {
        this.clockInSelectedSubordinates = clockInSubordinates;
        this.clockInSuccessCallback = this.completeMultiClockIn;
        this.clockInDialog = true;
      }
    },

    completeMultiClockIn(responseData) {
      this.updateSubordinateActiveTimePairs(
        this.clockInSelectedSubordinates,
        responseData,
      );

      this.clearSelections(this.clockInSelectedSubordinates);

      this.showSnackbar({
        text: this.$t('snackbar.clockInSuccessEmps', responseData.length, { empNo: responseData.length }),
        color: constants.AppColors.SUCCESS,
      });

      this.closeClockIn();
    },

    clockOutSelected(clockOutAll) {
      let clockOutSubordinates = [];
      if (clockOutAll) {
        clockOutSubordinates = this.getClockedInSubordinates(this.subordinateEmployees);
      } else {
        clockOutSubordinates = this.getClockedInSubordinates(this.getSelectedSubordinatesDetails());
      }

      if (clockOutSubordinates.length === 1) {
        this.individualClockOut(clockOutSubordinates[0]);
      } else {
        this.showAppConfirmDialog({
          message: `${this.$t('dialogs.confirm.clockEmployees', {
            empNo: clockOutSubordinates.length,
          })}`,
          confirmText: this.$t('clock.clockOut'),
          confirmHandler: () => this.submitMultiClockOut(clockOutSubordinates),
        });
      }
    },

    submitMultiClockOut(clockOutSubordinates) {
      this.showAppLoader(this.$tc('appLoader.clockingOutEmp', 2));

      const timeEntryRequests = [];
      forEach(clockOutSubordinates, (subordinate) => {
        timeEntryRequests.push({
          shiftTimePairId: subordinate.activeTimePair.id,
          timeZoneOffset: subordinate.activeTimePair.employeeAssignment.timeZoneOffset,
        });
      });

      this.createMassShiftClockOut(
        timeEntryRequests,
        (responseData) => {
          this.updateSubordinateActiveTimePairs(clockOutSubordinates, responseData);
          this.showSnackbar({
            text: this.$t('snackbar.clockOutSuccessEmps', { empNo: responseData.length }),
            color: constants.AppColors.SUCCESS,
          });
        },
        () => {
          this.clearSelections(clockOutSubordinates);
          this.hideAppLoader();
        },
      );
    },

    updateSubordinateActiveTimePairs(clockingSubordinates, responseData) {
      const errorSubordinates = [];
      forEach(clockingSubordinates, (subordinate) => {
        const newTimePair = find(
          responseData,
          (timePair) => some(subordinate.subordinateAssignments,
            { id: timePair.employeeAssignment.id }),
        );

        if (!newTimePair) {
          errorSubordinates.push(`${subordinate.employee.firstName} ${subordinate.employee.lastName}`);
          this.resetSubordinateEmployeeActiveTimePair({ employeeId: subordinate.employee.id });
        } else {
          this.updateSubordinateEmployeeActiveTimePair({
            employeeId: subordinate.employee.id,
            timePair: newTimePair,
          });
        }
      });

      if (errorSubordinates.length > 0) {
        this.showAppErrorDialog({
          message: this.$t(
            'dialogs.error.causes.shiftsHaveAlreadyClockedOut',
            { employees: errorSubordinates.join(', ') },
          ),
        });
      }
    },

    clearSelections(subordinates) {
      if (isEmpty(this.selectedSubordinates)) return;

      forEach(
        subordinates,
        (subordinate) => {
          if (this.selectedSubordinates.includes(subordinate.employee.id)) {
            this.selectedSubordinates.splice(
              this.selectedSubordinates.indexOf(subordinate.employee.id),
              1,
            );
          }
        },
      );

      this.selectedSubordinates = map(
        filter(this.orderedSubordinates,
          (sub) => this.selectedSubordinates.includes(sub.employee.id)),
        (sub) => sub.employee.id,
      );
    },

    viewTimePairMap(subordinate) {
      if (this.hasLocationData(subordinate.activeTimePair)) {
        this.viewingTimePair = subordinate.activeTimePair;
        this.timePairMapDialog = true;
      }
    },

    pingServerForActiveShifts() {
      this.loadSubordinateEmployeesActiveShift(this.decodedAuthToken)
      .then((response) => this.handleServerPingResponse(response))
      .finally(() => {
        this.pingServerTimeout = setTimeout(this.pingServerForActiveShifts, constants.SERVER_ACTIVE_SHIFT_PING_DELAY);
      });
    },

    handleServerPingResponse(response) {
      if (response.status === 200) {
        const activeShiftMap = response.data;
        Object.keys(activeShiftMap).forEach((key) => {
          const subordinate = this.getSubordinateByEmployeeId(key);
          if (!activeShiftMap[key]) {
            if (subordinate && subordinate.activeTimePair) {
              this.resetSubordinateEmployeeActiveTimePair({ employeeId: subordinate.employee.id });
            }
          } else {
            const returnedActiveShift = activeShiftMap[key];
            if (
              subordinate
              && (!subordinate.activeTimePair
                || (
                  subordinate.activeTimePair.id !== returnedActiveShift.id
                  || subordinate.activeTimePair.isSegmentPairActive !== returnedActiveShift.isSegmentPairActive
                ))
            ) {
              this.updateSubordinateEmployeeActiveTimePair({
                employeeId: subordinate.employee.id,
                timePair: returnedActiveShift,
              });
            }
          }
        });
      }
    },

    getSubordinateByEmployeeId(employeeId) {
      return this.subordinateEmployees.find((subordinate) => subordinate.employee.id === employeeId);
    },
  },

  created() {
    this.setPageTitle(this.$t('titles.manageEmpClocking'));

    this.subordinatesLoading = true;
    this.loadSubordinateEmployees().finally(() => {
      this.subordinatesLoading = false;
      this.loadEmployeeAbsences(
        this.subordinateEmployees.map((sub) => sub.subordinateAssignments[0].id),
      );
    });

    this.decodedAuthToken = AuthorisationUtil.fetchDecodedAuthToken();
    this.pingServerTimeout = setTimeout(this.pingServerForActiveShifts, constants.SERVER_ACTIVE_SHIFT_PING_DELAY);
  },

  beforeDestroy() {
    clearTimeout(this.pingServerTimeout);
  },
};
</script>

<style scoped>
.approverClockingSwitch {
  max-width: 48px;
}
</style>
